import React, { createContext, useContext, useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { describeBadgeError, fetchBatches } from "./badgesCrud";

const CheckInContext = createContext();

export function useCheckInContext() {
  return useContext(CheckInContext);
}

export const CheckInConsumer = CheckInContext.Consumer;

/**
 * The class list for the branch currently selected in the subheader.
 *
 * The kiosk app carried its own branch picker in the header; the portal already
 * has one (`Branch/BranchActions.js`, backed by `auth.branches.currentBranch`),
 * so this reads that instead of introducing a second place to choose a branch.
 * Switching branches reloads the list and — because the print screen is keyed
 * on the branch id — resets the sheet, the ticks and the filters to a clean
 * page rather than leaving one branch's badges under another branch's heading.
 */
export function CheckInContextProvider({ children }) {
  const { currentBranch, authToken } = useSelector(
    ({ auth }) => ({
      currentBranch: auth.branches.currentBranch,
      // Attached explicitly to every call below — see badgesCrud's header for
      // why axios.defaults cannot be trusted on a cold load.
      authToken: auth.authToken,
    }),
    shallowEqual
  );
  const branchId = currentBranch && currentBranch.branch_id;

  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    // -1 is the "No Branch Selected" sentinel from authRedux's initial state.
    // This effect runs on mount, so it is inside the rehydrate window: wait for
    // the token rather than firing a request that can only come back 401.
    if (!branchId || branchId === -1 || !authToken) {
      setBatches([]);
      return undefined;
    }
    let cancelled = false;
    const loadBatches = async () => {
      setIsLoading(true);
      setErrorMessage("");
      try {
        const rows = await fetchBatches(branchId, authToken);
        if (!cancelled) setBatches(rows);
      } catch (error) {
        if (cancelled) return;
        setBatches([]);
        setErrorMessage(
          describeBadgeError(error, "Could not load classes for this branch.")
        );
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadBatches();
    return () => {
      cancelled = true;
    };
  }, [branchId, authToken]);

  const checkInContext = {
    BRANCH: {
      currentBranch,
      branchId,
    },
    BATCH: {
      batches,
    },
    states: {
      isLoading,
      errorMessage,
      setErrorMessage,
    },
  };

  return (
    <CheckInContext.Provider value={checkInContext}>
      {children}
    </CheckInContext.Provider>
  );
}
